import { EmailDomain } from "@/types/EmailDomain";
import { EmailDomainState } from "@/types/enums/EmailDomainState";
import { EmailIdentityType } from "@/types/enums/EmailIdentityType";
import { SenderAddress } from "@/types/SenderAddress";

/**
 * Mirror of main-consumer's `SenderResolver` for DISPLAY purposes.
 *
 * The worker is the only thing that decides a message's real `From:`
 * (main-consumer/src/lib/sender-resolver.ts:351-398 `resolveFromAddressForTemplate`).
 * Its chain is:
 *
 *   1. `template.senderMail` — only when it resolves AND its domain is VERIFIED
 *   2. the event PRIMARY sender (the star in the sender settings table)
 *   3. `DEFAULT_FROM`
 *
 * The UI used to model only step 1 and printed a hardcoded
 * `"no-reply@veltric.app"` for everything else, so a tenant with a starred
 * verified sender was told their invitation went out as Veltric while it
 * actually went out as theirs. That literal was doubly wrong: the real default
 * has no hyphen and carries a display name.
 *
 * Keep this file in lockstep with the consumer. The two gates below are the
 * parts that are easy to forget, because the data for them is loaded already
 * and simply was not being read.
 */

/** Verbatim from main-consumer/src/lib/sender-resolver.ts:41. */
export const DEFAULT_FROM = "Veltric <noreply@veltric.app>";

/** Verbatim from main-consumer/src/lib/sender-resolver.ts:42. */
export const FORCED_DISPLAY_NAME = "Veltric";

/**
 * Form-state value for "no override — use whatever the event/company resolves
 * to". Deliberately a visible sentinel and NOT the empty string: a picker whose
 * "use the default" case rides on a falsy value behaves differently the moment
 * an input blurs, a draft rehydrates as `undefined`, or a field is cleared —
 * three accidents that all look like a deliberate choice. On the wire it is
 * still translated to `""`, which is what the backend reads as "clear".
 */
export const EVENT_SENDER_SENTINEL = "__event-sender__";

/**
 * Form-state value for "send as the platform default, ignore the event's star".
 *
 * NOT a synonym for {@link EVENT_SENDER_SENTINEL}. That one means "drop the
 * override and re-enter the resolver chain", whose last rung is only reached
 * when no primary sender exists — and since the backend auto-stars the first
 * sender an event gets, and offers no un-star, `noreply@veltric.app` became
 * unreachable the moment a tenant added an address of their own. This value
 * travels as a real persisted boolean (`platformSenderEnabled`), not as `""`.
 */
export const PLATFORM_SENDER_SENTINEL = "__platform-sender__";

/**
 * True for either sentinel.
 *
 * Exists so that "is this a sentinel?" can never again be written as an
 * equality against ONE of them. Three separate guards in the picker
 * (`deletedSenderId`, `strandedSender`, form validation) compared literally
 * against the event sentinel; a second sentinel reaching them unrecognised is
 * classified as a 24-hex id matching no sender, i.e. reported to the user as a
 * DELETED sender on every platform-pinned template.
 */
export function isSenderSentinel(value: string | undefined | null): boolean {
  return value === EVENT_SENDER_SENTINEL || value === PLATFORM_SENDER_SENTINEL;
}

/** Which rung of the chain produced the address. */
export type SenderSource = "template" | "eventPrimary" | "default" | "platform";

export type EffectiveSender = {
  /** Exactly what the recipient will see in `From:`. */
  address: string;
  source: SenderSource;
  /**
   * Set when the resolved sender is an ADDRESS row that goes out ON BEHALF
   * (see {@link isOnBehalf}): `from` is the rewritten mailbox (equal to
   * `address`), `replyTo` the tenant's real address, which the consumer puts in
   * `Reply-To` unless the template's own `replyToMail` is set — callers that
   * know the template prefer that one.
   */
  onBehalf?: { from: string; replyTo: string };
};

/**
 * Mirrors main-consumer's `canSendFromDomain` (email-domain-repo.ts) and the
 * `$or` inside the resolver's join.
 *
 * `emailDomainState` alone is not enough for a row flagged
 * `ownershipProofRequired`: SES identities are shared by every company that
 * registered the same domain string, so that state can be a read of SOMEONE
 * ELSE's published DKIM. The per-tenant proof is the `dnsCustom` TXT token
 * (`dnsStateCustom`). Rows without the flag are untouched.
 */
export function isSendableDomain(domain: EmailDomain): boolean {
  if (domain.emailDomainState !== EmailDomainState.VERIFIED) return false;
  if (domain.ownershipProofRequired !== true) return true;
  return domain.dnsStateCustom === EmailDomainState.VERIFIED;
}

// ── Sender on behalf (2026-09-29) ─────────────────────────────────────────
//
// An ADDRESS row whose host has no verified DOMAIN row in the SES account is
// signed by SES with `amazonses.com`, so DMARC fails on any strict host and
// Gmail/GMX file the mail as spam. The consumer therefore rewrites such a
// sender (main-consumer `on-behalf.ts`):
//
//   From:     "Name" <info=triggerschmiede.com@send.mail-veltric.app>
//   Reply-To: info@triggerschmiede.com   (the template's replyToMail wins)
//
// Everything below mirrors that decision for DISPLAY. The rewrite itself is
// byte-for-byte parity-pinned against the consumer through
// `__tests__/on-behalf-parity.json` (two copies, one hash).

/**
 * The on-behalf domain, or null while the feature is off in this build.
 *
 * Read INSIDE the function, never hoisted into a module constant: `vi.stubEnv`
 * replaces `import.meta.env` per test, and a value captured at import time
 * would pin every test to whatever the first import saw (same move as
 * `isClicksModeOffered`). Set in `.env.production` only AFTER the consumer runs
 * with `SENDER_ON_BEHALF_MODE=enforce` — before that the UI would announce a
 * rewrite the worker does not do.
 */
export function onBehalfDomain(): string | null {
  const raw: unknown = import.meta.env.VITE_SENDER_ON_BEHALF_DOMAIN;
  if (typeof raw !== "string") return null;
  const domain = raw.trim().toLowerCase();
  return domain === "" ? null : domain;
}

/** Host of an address literal: after the LAST `@`, trimmed + lower-cased. */
export function addressHost(address: string): string {
  const value = String(address ?? "").trim().toLowerCase();
  return value.slice(value.lastIndexOf("@") + 1);
}

/**
 * The DOMAIN row (never an ADDRESS row) registered for exactly this host.
 *
 * Exact host only, like the coverage filter: SES would sign `a@sub.firma.de`
 * with `firma.de`'s DKIM, but a strict `adkim=s` still fails that alignment,
 * so a parent domain is no cover — and no row to offer either.
 */
export function findDomainRowForHost(
  domains: EmailDomain[],
  host: string,
): EmailDomain | undefined {
  const wanted = host.trim().toLowerCase();
  if (wanted === "") return undefined;
  return domains.find(
    (d) => !isAddressIdentity(d) && String(d.domain ?? "").trim().toLowerCase() === wanted,
  );
}

/**
 * The loaded list holds a VERIFIED DOMAIN row for exactly this host.
 *
 * Same shape as the consumer/Java coverage filter
 * (`{domain: host, identityType: {$ne: 'ADDRESS'}, emailDomainState: 'VERIFIED'}`),
 * minus the account-wide reach: the list is scoped, so a miss proves nothing.
 * A HIT, however, is proof — and it bridges the seconds between a domain
 * turning VERIFIED here and the consumer's recompute job flipping the stored
 * `dkimCovered` of the address rows.
 */
function listShowsVerifiedDomainFor(domains: EmailDomain[], host: string): boolean {
  return domains.some(
    (d) =>
      !isAddressIdentity(d) &&
      d.emailDomainState === EmailDomainState.VERIFIED &&
      String(d.domain ?? "").trim().toLowerCase() === host,
  );
}

/**
 * Does a verified domain cover this ADDRESS row's signature?
 *
 * The stored `dkimCovered === true`, or — bridging a stale `false` — a
 * VERIFIED DOMAIN row for the exact host in the loaded list. No build flag:
 * this is a fact about the domain, not about how the consumer sends. The
 * address dialog's Done step reads it before saying "verified through the
 * verified domain X" (audit r2 R2FE-2) — once X no longer covers the
 * address, that line would contradict the on-behalf block right under it.
 */
export function isDomainCovered(row: EmailDomain, domains?: EmailDomain[]): boolean {
  if (row.dkimCovered === true) return true;
  return !!domains && listShowsVerifiedDomainFor(domains, addressHost(row.domain));
}

/**
 * Would this ADDRESS row go out on behalf once it may send at all?
 *
 * The rule without the sendability gate — for the address dialog's link step,
 * where the row is not VERIFIED yet but the user should already learn how its
 * mail will look. Everything that previews a REAL send uses {@link isOnBehalf}.
 *
 * `dkimCovered !== true`, not `=== false`: a missing field (a row read through
 * a projection that dropped it, or a backend rollback) is the safe side —
 * "on behalf" — exactly as in the consumer.
 */
export function wouldSendOnBehalf(row: EmailDomain, domains?: EmailDomain[]): boolean {
  if (onBehalfDomain() === null) return false;
  if (!isAddressIdentity(row)) return false;
  return !isDomainCovered(row, domains);
}

/**
 * Does a sender on this identity row go out ON BEHALF?
 *
 * The consumer's rule (`SENDER_ON_BEHALF_MODE=enforce`, domain set, ADDRESS
 * row, sendable, `dkimCovered !== true`) with the build flag standing in for
 * the first two. DOMAIN rows, the platform sender and the default are never
 * rewritten. Pass the loaded `domains` wherever they are at hand: a VERIFIED
 * DOMAIN row for the exact host in that list overrides a stale `false`.
 *
 * What the UI cannot see: the consumer's kill switch, trip and daily cap, under
 * which it sends as the address itself or as `noreply@veltric.app` instead.
 * The send dialogs say so in words rather than pretend to know.
 */
export function isOnBehalf(row: EmailDomain, domains?: EmailDomain[]): boolean {
  if (!isSendableDomain(row)) return false;
  return wouldSendOnBehalf(row, domains);
}

/** SHA-256 round constants (FIPS 180-4 §4.2.2). */
const SHA256_K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

/**
 * SHA-256 over the UTF-8 bytes, the first 80 bits as 20 hex digits — the
 * consumer twin runs `node:crypto`; this one must stay synchronous for the
 * render path, so it is plain FIPS 180-4, pinned against node:crypto in the
 * tests. Not FNV-1a (the first cut): FNV is invertible, and a tenant could
 * craft an address that lands on another tenant's local part (audit r6, R6S-1).
 */
function sha256Hex80(value: string): string {
  const bytes = new TextEncoder().encode(value);
  const blocks = new Uint8Array(((bytes.length + 9 + 63) >> 6) << 6);
  blocks.set(bytes);
  blocks[bytes.length] = 0x80;
  const view = new DataView(blocks.buffer);
  const bits = bytes.length * 8;
  view.setUint32(blocks.length - 8, Math.floor(bits / 0x100000000));
  view.setUint32(blocks.length - 4, bits >>> 0);
  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);
  for (let off = 0; off < blocks.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = w[i - 16] + s0 + w[i - 7] + s1;
    }
    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i++) {
      const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + SHA256_K[i] + w[i]) | 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }
    h[0] += a;
    h[1] += b;
    h[2] += c;
    h[3] += d;
    h[4] += e;
    h[5] += f;
    h[6] += g;
    h[7] += hh;
  }
  return Array.from(h, (x) => x.toString(16).padStart(8, "0"))
    .join("")
    .slice(0, 20);
}

/** The local-part alphabet the rewritten address may carry. */
const ON_BEHALF_LOCAL_PART = /^[a-z0-9._+=-]+$/;

/**
 * Local part of the rewritten sender: `info@triggerschmiede.com` →
 * `info=triggerschmiede.com`.
 *
 * Anything that would not make a clean dot-atom (a `%`, a leading/trailing or
 * doubled dot, non-ASCII, over 64 chars, no usable `@`) becomes `u-<sha256-hex20>`
 * instead. Never `noreply`: the result always carries `=` or `u-`. Verbatim
 * twin of main-consumer's `onBehalfLocalPart` — change both, and the fixture.
 */
export function onBehalfLocalPart(address: string): string {
  const value = String(address ?? "").trim().toLowerCase();
  const at = value.lastIndexOf("@");
  if (at > 0 && at < value.length - 1) {
    const candidate = `${value.slice(0, at)}=${value.slice(at + 1)}`;
    if (
      ON_BEHALF_LOCAL_PART.test(candidate) &&
      !candidate.startsWith(".") &&
      !candidate.endsWith(".") &&
      !candidate.includes("..") &&
      candidate.length <= 64
    ) {
      return candidate;
    }
  }
  return `u-${sha256Hex80(value)}`;
}

/** RFC 5322 specials (plus `.`) that force a quoted display name. */
const MAILBOX_SPECIALS = /[()<>[\]:;@\\,."]/;

/** An RFC 2047 encoded-word opener (`=?charset?B?` / `=?charset?Q?`). */
const ENCODED_WORD = /=\?[^?]*\?[bq]\?/i;

/**
 * Would this name read as an address in the recipient's client? An `@` after
 * NFKC (so the fullwidth `＠` counts), or an encoded-word, which a client
 * decodes back into whatever it hides — `=?utf-8?Q?ceo=40bigbrand=2Ecom?=` is
 * `ceo@bigbrand.com` on screen (security audit round 1, L5-1).
 */
function looksLikeAddress(name: string): boolean {
  return name.normalize("NFKC").includes("@") || ENCODED_WORD.test(name);
}

/**
 * RFC 5322 specials MINUS `.` — the twin of main-consumer `header-safe.ts`
 * `quoteDisplayName` (Welle Z 1b). The ordinary sender's name is quoted only
 * for these, so "… Co. KG" stays byte-identical while `Müller, Test` can no
 * longer read as two mailboxes.
 */
const DISPLAY_NAME_SPECIALS = /[()<>[\]:;@\\,"]/;

/**
 * The ordinary (non-on-behalf) display name exactly as the consumer writes it:
 * C0 controls and DEL become spaces, trimmed, then a quoted-string (with `"`
 * and `\` escaped) when it carries one of {@link DISPLAY_NAME_SPECIALS}. An
 * empty name stays empty — the caller then shows the bare address.
 */
export function quoteDisplayName(name: string): string {
  // eslint-disable-next-line no-control-regex -- the controls ARE the target
  const n = name.replace(/[\u0000-\u001f\u007f]+/g, " ").trim();
  if (!DISPLAY_NAME_SPECIALS.test(n)) return n;
  return `"${n.replace(/(["\\])/g, "\\$1")}"`;
}

/**
 * `display <addr>` exactly as the consumer composes the on-behalf `From:`.
 *
 * - C0 controls and DEL become spaces, then the name is trimmed (a tab or a
 *   vertical tab has no business in a header).
 * - A name that looks like an address (see `looksLikeAddress`) is dropped — it
 *   would read as a second sender — unless it IS the real address,
 *   case-insensitively. The name shown is NOT NFKC-normalised, only tested.
 * - An empty name becomes the real address, so the recipient still reads
 *   `"info@triggerschmiede.com" <info=…@send…>` rather than a bare stranger.
 * - Specials make it a quoted-string with `"` and `\` escaped; non-ASCII stays
 *   raw here (RFC 2047 happens only in the consumer's MIME layer).
 */
export function formatMailbox(
  name: string | null | undefined,
  addr: string,
  realAddress: string,
): string {
  // eslint-disable-next-line no-control-regex -- the controls ARE the target
  let display = String(name ?? "").replace(/[\u0000-\u001f\u007f]/g, " ").trim();
  const real = String(realAddress ?? "").trim();
  if (looksLikeAddress(display) && display.toLowerCase() !== real.toLowerCase()) display = "";
  if (display === "") display = real;
  const shown = MAILBOX_SPECIALS.test(display)
    ? `"${display.replace(/(["\\])/g, "\\$1")}"`
    : display;
  return shown === "" ? addr : `${shown} <${addr}>`;
}

/**
 * The bare, lower-cased address of a STORED mailbox (`"Name" <a@b.de>` or
 * `a@b.de`): the part inside a trailing `<…>` when there is one, else the
 * whole trimmed value. Empty for a missing or blank value.
 */
export function mailboxAddress(mailbox: string | null | undefined): string {
  const trimmed = String(mailbox ?? "").trim();
  const angle = trimmed.match(/<([^<>]+)>\s*$/);
  return (angle ? angle[1] : trimmed).trim().toLowerCase();
}

/**
 * Was a STORED `From:` (a sent mail's `senderMail`) the on-behalf rewrite?
 * Reads the address inside `<…>` when there is one — the stored value carries
 * the display name — and matches the on-behalf domain exactly.
 */
export function isOnBehalfFrom(from: string | null | undefined): boolean {
  const domain = onBehalfDomain();
  if (domain === null || !from) return false;
  return mailboxAddress(from).endsWith(`@${domain}`);
}

/**
 * The bare rewritten address (`info=triggerschmiede.com@send.mail-veltric.app`),
 * or null while the feature is off.
 */
export function onBehalfAddress(realAddress: string): string | null {
  const domain = onBehalfDomain();
  if (domain === null) return null;
  return `${onBehalfLocalPart(realAddress)}@${domain}`;
}

/**
 * `local@domain`, or null when the sender cannot be used at all.
 *
 * Null mirrors the consumer's `$lookup` sub-pipeline
 * (sender-resolver.ts): the join filters on `emailDomainState: 'VERIFIED'`
 * plus proven ownership (see {@link isSendableDomain}) and a non-matching row
 * is dropped by the `$unwind`, surfacing as null so the caller falls through
 * to the next rung.
 * A missing domain is treated the same way — it cannot be proven verified.
 *
 * Note the old display code fell back to the BARE local part when the domain
 * was absent, which rendered a half address (`HeidiFest <info>`) that no send
 * path would ever produce.
 */
/**
 * True for a single-address identity row (`identityType: 'ADDRESS'`).
 *
 * The `@` fallback mirrors the consumer's `composeLocalAt`: a row written by a
 * newer backend but read through a projection that dropped `identityType`
 * still carries the full address in `domain`, and gluing a local part in front
 * of it would print `max@max@firma.de` — an address no send path produces.
 */
export function isAddressIdentity(domain: Pick<EmailDomain, "identityType" | "domain">): boolean {
  return (
    domain.identityType === EmailIdentityType.ADDRESS ||
    (typeof domain.domain === "string" && domain.domain.includes("@"))
  );
}

/**
 * The literal `From:` address a sender on this identity row sends as.
 *
 * DOMAIN rows: `local@domain`. ADDRESS rows: the row's own `domain` (already
 * the full address); the sender's local part is forced equal to the row's by
 * the backend, so it is ignored here exactly as the consumer ignores it.
 *
 * Every place that composes an address from a sender + its domain row goes
 * through this — never `${email}@${domain}` inline.
 */
export function identityLiteral(domain: EmailDomain, localPart: string): string {
  if (isAddressIdentity(domain)) return domain.domain;
  return `${localPart}@${domain.domain}`;
}

/** How long an AWS confirmation link stays clickable. */
export const ADDRESS_LINK_TTL_MS = 24 * 60 * 60 * 1000;

const LINK_EXPIRED_REASONS = new Set([
  "LINK_NOT_CONFIRMED",
  "IDENTITY_MISSING",
  "VALIDATE_EXHAUSTED",
  "ROLLBACK",
]);

/**
 * Status key of an ADDRESS row. The order is load-bearing:
 *
 *   dmarcReject → authBounce → VERIFIED → dmarcUnknown → otpPending →
 *   linkExpired → linkPending
 *
 * The two kill reasons outrank everything (a row killed for DMARC or for auth
 * bounces must never read "Verified", even if a stale state field says so);
 * VERIFIED then means BOTH proofs are in (the tenant's code AND SES + DMARC);
 * an unresolved DMARC policy blocks before the code/link steps because neither
 * of them can lift it.
 */
function addressStateLabelKey(domain: EmailDomain, now: number): string {
  if (domain.failureReason === "DMARC_REJECT" || domain.dmarcPolicy === "reject") {
    return "dmarcReject";
  }
  if (domain.failureReason === "AUTH_BOUNCE") return "authBounce";
  if (
    domain.emailDomainState === EmailDomainState.VERIFIED &&
    domain.dnsStateCustom === EmailDomainState.VERIFIED
  ) {
    return EmailDomainState.VERIFIED;
  }
  if (domain.dmarcPolicy === "unknown" || domain.failureReason === "DMARC_UNKNOWN") {
    return "dmarcUnknown";
  }
  if (domain.dnsStateCustom !== EmailDomainState.VERIFIED) return "otpPending";
  if (domain.failureReason && LINK_EXPIRED_REASONS.has(domain.failureReason)) {
    return "linkExpired";
  }
  const linkFrom = domain.sesLinkSentAt ?? domain.proofVerifiedAt;
  if (typeof linkFrom === "number" && linkFrom + ADDRESS_LINK_TTL_MS < now) {
    return "linkExpired";
  }
  return "linkPending";
}

/**
 * How long SES keeps looking for a domain's DKIM records after a (re)start of
 * the search — and therefore how long a worker answer of "still looking" may
 * be believed without a newer one.
 */
export const SES_SEARCH_WINDOW_MS = 72 * 60 * 60 * 1000;

/** Worker answers that mean "SES has not said no (again) yet". */
const AWAITING_SES_OUTCOMES: ReadonlySet<string> = new Set([
  "RETRIGGERED",
  "PENDING",
  "EXHAUSTED",
]);

/**
 * Is SES still searching for this DOMAIN row's records?
 *
 * `EmailDomainState` has no PENDING, so a domain SES is looking for right now
 * reads `FAILED` — the same word as one whose records are plainly wrong. The
 * worker says which of the two it is in `sesCheckOutcome`; all three must
 * hold:
 *
 * - the row is `FAILED` (a VERIFIED row has nothing to wait for, whatever an
 *   older outcome says),
 * - the worker's last answer was one of the "not decided" ones — it restarted
 *   the search (`RETRIGGERED`), SES had no verdict (`PENDING`), or the retry
 *   ladder ran out before one came (`EXHAUSTED`),
 * - and that answer is younger than SES's own 72-hour search window.
 *
 * The age cut-off is what keeps "Pending" from becoming permanent: the daily
 * refresh re-stamps `sesCheckedAt` only while it watches a row (30 days, and
 * only in production), so without it a row the refresh dropped — or any row on
 * staging — would say "Pending" forever over a search that ended long ago. A
 * missing or unreadable stamp counts as old.
 *
 * ADDRESS rows never: they carry no DKIM records of their own and have their
 * own label chain ({@link addressStateLabelKey}).
 *
 * DISPLAY only. An awaiting row is exactly as unsendable as any other FAILED
 * one — {@link isSendableDomain} does not read any of this.
 */
export function isDomainAwaitingSes(domain: EmailDomain, now: number = Date.now()): boolean {
  if (isAddressIdentity(domain)) return false;
  if (domain.emailDomainState !== EmailDomainState.FAILED) return false;
  if (!domain.sesCheckOutcome || !AWAITING_SES_OUTCOMES.has(domain.sesCheckOutcome)) return false;
  if (typeof domain.sesCheckedAt !== "string") return false;
  const checkedAt = Date.parse(domain.sesCheckedAt);
  if (Number.isNaN(checkedAt)) return false;
  return now - checkedAt < SES_SEARCH_WINDOW_MS;
}

/**
 * The status word a domain row should be shown with.
 *
 * A row whose SES state is VERIFIED but whose own ownership token is not
 * published is NOT usable, and printing "Verified" next to it is the same
 * failure the modal's status dot had: a green promise over mail that the
 * send gate refuses. Callers translate the returned key in the `sender`
 * namespace (both label sites live there).
 *
 * A FAILED row SES is still searching for (see {@link isDomainAwaitingSes})
 * reads `"PENDING"` — a plain string key, not an enum member: the FE enum has
 * none, while `sender.PENDING` and `pAddDomain.PENDING` exist in every locale.
 * "Error" there would be the opposite lie to the one above: a verdict nobody
 * has given yet.
 */
export function domainStateLabelKey(
  domain: EmailDomain,
  now: number = Date.now(),
): string {
  if (isAddressIdentity(domain)) return addressStateLabelKey(domain, now);
  if (domain.emailDomainState === EmailDomainState.VERIFIED && !isSendableDomain(domain)) {
    return "ownershipProofPending";
  }
  if (isDomainAwaitingSes(domain, now)) return "PENDING";
  return domain.emailDomainState;
}

function resolveLocalAt(
  sender: SenderAddress,
  domains: EmailDomain[],
): string | null {
  const domain = domains.find((d) => d.emailDomainId === sender.emailDomainId);
  if (!domain) return null;
  if (!isSendableDomain(domain)) return null;
  return identityLiteral(domain, sender.email);
}

export type FormatSenderOptions = {
  /**
   * Render what the RECIPIENT sees. For an on-behalf row that is the rewritten
   * mailbox (`Veltric <info=firma.de@send.mail-veltric.app>`). Every preview of
   * a real send goes through {@link resolveEffectiveSender}, which sets this.
   */
  preview?: boolean;
  /**
   * Picker labels only (`preview` off): the translated "on behalf" word,
   * appended as ` (<label>)` to an on-behalf row. The label keeps the REAL
   * address in front so typing `info@…` into the picker still finds it.
   */
  onBehalfLabel?: string;
};

/**
 * Compose `"DisplayName <local@domain>"`, applying the white-label gate.
 *
 * Mirrors `compose` + `resolveDisplayName` (sender-resolver.ts:403-419):
 * below Advanced the tenant's alias is DISCARDED and replaced by "Veltric".
 * With white-labeling and no alias configured, the display name is empty and
 * the bare address is used.
 *
 * On-behalf rows (see {@link isOnBehalf}) differ by mode: a preview renders
 * the consumer's rewritten mailbox, a picker label the real address plus the
 * `onBehalfLabel` suffix.
 *
 * @returns null when the sender is unusable (see {@link resolveLocalAt}).
 */
export function formatSenderAddress(
  sender: SenderAddress,
  domains: EmailDomain[],
  hasWhiteLabel: boolean | null,
  options: FormatSenderOptions = {},
): string | null {
  return composeSender(sender, domains, hasWhiteLabel, options)?.address ?? null;
}

function composeSender(
  sender: SenderAddress,
  domains: EmailDomain[],
  hasWhiteLabel: boolean | null,
  { preview = false, onBehalfLabel }: FormatSenderOptions,
): { address: string; onBehalf?: { from: string; replyTo: string } } | null {
  const localAt = resolveLocalAt(sender, domains);
  if (localAt === null) return null;
  const row = domains.find((d) => d.emailDomainId === sender.emailDomainId);
  const rewritten = row && isOnBehalf(row, domains) ? onBehalfAddress(localAt) : null;

  // `null` = the plan is UNKNOWN (still loading, or the caller may not read
  // subscriptions — the endpoint is OWNER/ADMIN only while these surfaces are
  // open to editors). Naming a sender then would be a guess in one direction
  // or the other, so the address is shown bare: everything printed is true,
  // just less of it. On behalf that is the bare rewritten address — the
  // consumer's display name is one of three and none of them is knowable yet.
  const displayName =
    hasWhiteLabel === null ? null : hasWhiteLabel ? (sender.alias ?? "") : FORCED_DISPLAY_NAME;

  if (rewritten !== null && preview) {
    const from = displayName === null ? rewritten : formatMailbox(displayName, rewritten, localAt);
    return { address: from, onBehalf: { from, replyTo: localAt } };
  }

  // A preview reads the From the consumer writes, which quotes the ordinary
  // name since Welle Z 1b; a picker label keeps the name as typed.
  const shownName =
    displayName === null ? "" : preview ? quoteDisplayName(displayName) : displayName;
  const plain = shownName.length > 0 ? `${shownName} <${localAt}>` : localAt;
  if (rewritten !== null && onBehalfLabel) return { address: `${plain} (${onBehalfLabel})` };
  return { address: plain };
}

export type ResolveEffectiveSenderInput = {
  /** `template.senderMail` — a senderAddressId, NOT an address. */
  templateSenderMail: string | undefined;
  senderAddresses: SenderAddress[];
  domains: EmailDomain[];
  /** `null` = plan unknown; the display name is omitted rather than guessed. */
  hasWhiteLabel: boolean | null;
  /**
   * `template.platformSenderEnabled` — an explicit "always the platform
   * default", which short-circuits the whole chain. Mirrors the consumer
   * (sender-resolver.ts), including its position ABOVE the template rung.
   */
  platformSenderEnabled?: boolean;
  /**
   * Company-template lane opt-out. A company email template has no `eventId`
   * and therefore no event primary to fall back to, so its chain is legitimately
   * two-rung (template sender → default). Defaults to the event chain.
   */
  allowEventPrimary?: boolean;
};

/**
 * The address that will really be used, and which rung produced it.
 *
 * Callers render `address` verbatim — it is already gated and composed.
 */
export function resolveEffectiveSender({
  templateSenderMail,
  senderAddresses,
  domains,
  hasWhiteLabel,
  allowEventPrimary = true,
  platformSenderEnabled = false,
}: ResolveEffectiveSenderInput): EffectiveSender {
  // Rung 0, above the template rung — same order as the worker
  // (main-consumer/src/lib/sender-resolver.ts). A template carrying both a pin
  // and the flag is corrupt by backend invariant; resolving it toward the
  // neutral address is the harmless direction, and matching the worker is what
  // keeps this display honest.
  //
  // `DEFAULT_FROM` verbatim, NOT through `formatSenderAddress`: that one
  // applies the white-label gate, which would print the tenant's own alias in
  // front of the platform address for Advanced plans — an address string no
  // send path produces.
  if (platformSenderEnabled) {
    return { address: DEFAULT_FROM, source: "platform" };
  }

  // `preview: true` on both rungs: this is what the recipient sees, so an
  // on-behalf row resolves to its rewritten mailbox and reports the real
  // address as `onBehalf.replyTo`.
  if (templateSenderMail) {
    const own = senderAddresses.find(
      (addr) => addr.senderAddressId === templateSenderMail,
    );
    if (own) {
      const composed = composeSender(own, domains, hasWhiteLabel, { preview: true });
      if (composed !== null) return { ...composed, source: "template" };
    }
  }

  if (allowEventPrimary) {
    const primary = senderAddresses.find((addr) => addr.primary);
    if (primary) {
      const composed = composeSender(primary, domains, hasWhiteLabel, { preview: true });
      if (composed !== null) return { ...composed, source: "eventPrimary" };
    }
  }

  return { address: DEFAULT_FROM, source: "default" };
}

/**
 * The senders a picker may legitimately offer: those that would actually
 * survive the send path. Anything this drops would silently fall through to a
 * different address at send time, which is exactly the surprise being fixed.
 */
export function selectableSenderAddresses(
  senderAddresses: SenderAddress[],
  domains: EmailDomain[],
): SenderAddress[] {
  return senderAddresses.filter(
    (addr) => resolveLocalAt(addr, domains) !== null,
  );
}

/**
 * Senders whose domain was READ but is not VERIFIED.
 *
 * A picker may offer these; a send path may not use them. The distinction
 * exists because `EmailDomainState` has no PENDING: a domain sitting in its
 * 72-hour DNS propagation window reads `FAILED`, exactly like one that will
 * never verify. Dropping them from the picker meant a tenant who had just
 * added a domain saw nothing but the "use the event's sender" entry and could
 * not preconfigure a template at all — while the domain was, in fact, on its
 * way to working.
 *
 * They are offered LABELLED (`senderUnverifiedOption`), never silently, and
 * {@link resolveEffectiveSender} keeps gating them out, so the From line still
 * falls to the next rung for as long as the domain stays unverified. Picking
 * one is a statement of intent about the future, not a claim about today.
 */
export function unverifiedSenderAddresses(
  senderAddresses: SenderAddress[],
  domains: EmailDomain[],
): SenderAddress[] {
  return senderAddresses.filter((addr) => {
    if (resolveLocalAt(addr, domains) !== null) return false;
    // A missing domain row is NOT this case: nothing can be said about it, and
    // printing `local@?` would be the half address this module exists to kill.
    return domains.some((d) => d.emailDomainId === addr.emailDomainId);
  });
}

/**
 * Would the bulk-send worker refuse this job over the pinned sender's domain?
 *
 * Mirrors `bulk-email-fanout.ts` exactly: the worker only checks when the job
 * payload carries an `emailDomainId` at all, and it fails the WHOLE job when
 * the domain resolves and is not VERIFIED. The picker offers unverified senders
 * on purpose (a domain in its 72-hour DNS window is indistinguishable from a
 * dead one), so this state is reachable by design — and today it is discovered
 * by the job dying mid-run rather than before it starts.
 *
 * Returns false when the domain row was NOT read. That case must WARN, never
 * block: locking a working send behind data the client was never given is a
 * worse failure than the one being prevented, and after the meta handler's
 * domain top-up it is nearly extinct anyway.
 */
export function isPinnedSenderDomainUnverified(
  emailDomainId: string | undefined,
  domains: EmailDomain[],
): boolean {
  if (!emailDomainId) return false;
  const domain = domains.find((d) => d.emailDomainId === emailDomainId);
  if (!domain) return false;
  return !isSendableDomain(domain);
}

/**
 * Senders whose domain row was NOT read at all.
 *
 * The exact complement of {@link selectableSenderAddresses} and
 * {@link unverifiedSenderAddresses}: together the three partition the input.
 * These are the senders that used to vanish from every surface without a label
 * — dropped by the first because the domain cannot be proven verified, and by
 * the second because it explicitly requires the domain row to exist.
 *
 * Nothing here can be printed as an address (there is no domain to print), so
 * callers may only COUNT these or name their alias — never compose a literal.
 */
export function unresolvableSenderAddresses(
  senderAddresses: SenderAddress[],
  domains: EmailDomain[],
): SenderAddress[] {
  return senderAddresses.filter(
    (addr) =>
      resolveLocalAt(addr, domains) === null &&
      !domains.some((d) => d.emailDomainId === addr.emailDomainId),
  );
}

/**
 * `local@domain` for a sender whose domain row was read, regardless of its
 * verification state — the literal to show next to an "unverified" label.
 *
 * Returns null when the domain row is absent, because then there is no address
 * to print, only a guess.
 */
export function senderAddressLiteral(
  sender: SenderAddress,
  domains: EmailDomain[],
): string | null {
  const domain = domains.find((d) => d.emailDomainId === sender.emailDomainId);
  return domain ? identityLiteral(domain, sender.email) : null;
}
